import { apiGet } from '@/features/shared/utils/dataFetching'
import {
  type UniversalProductError,
  type FetchUniversalProductParams,
  type UniversalProductResponse,
} from '@/features/shop/services/UniversalProduct/types'
import { normalizeUniversalProductResponse } from '@/features/shop/services/UniversalProduct/utils'
import { type QueryFunctionContext } from '@tanstack/react-query'
import { type UniversalProductQueryKey } from '@/features/shop/services/UniversalProduct/queries'
import { isAxiosError, type AxiosError } from 'axios'

type Context = QueryFunctionContext<UniversalProductQueryKey>

export const isUniversalProductErrorWithDetails = (
  e: unknown,
): e is AxiosError<UniversalProductError> => {
  return (
    isAxiosError(e) &&
    e.response?.status === 404 &&
    'error' in e.response.data &&
    e.response.data.error.details &&
    e.response.data.error.message === 'Could not find product'
  )
}

export const fetchUniversalProduct = async (context: Context) => {
  const [, { universal_id, address, region_id, refetchDisabled }] = context.queryKey
  let response: UniversalProductResponse
  // Using try catch to refetch again in case of invalid zip code
  // only if the error message is not "Could not find product" or refetchDisabled is false (default option)
  try {
    const params: FetchUniversalProductParams = {
      region_id,
      universal_id,
      additional_error_details: true,
      ...(address.zip && address),
    }
    response = await fetchDetails(params, context)
  } catch (error) {
    if (refetchDisabled) throw error
    if (isUniversalProductErrorWithDetails(error)) {
      const deletedAt = error.response?.data.error.details.up_deleted_at
      const sixMonthsAgo = new Date()
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)

      if (deletedAt && new Date(deletedAt) > sixMonthsAgo) {
        error.status = 301
      } else {
        error.status = 410
      }

      throw error
    }

    response = await fetchDetails({ universal_id, additional_error_details: true }, context)
  }
  return normalizeUniversalProductResponse(response)
}

const fetchDetails = (params: FetchUniversalProductParams, context: Context) =>
  apiGet<UniversalProductResponse>({
    config: {
      url: '/universal-products/v1/product',
      params,
    },
    options: {
      includeAuthHeader: false,
      ignoredMessages: [
        'Product currently unavailable in your area',
        'Please enter a valid zip code', // user bypasses validation to use an invalid zip code
      ],
    },
    context,
    fetcherName: 'fetchDetails',
  })
